import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  InputLabel,
  ListSubheader,
  Paper,
  TextField,
} from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box } from '@mui/system'
import { useDispatch } from 'react-redux'
import { debounce, isNotEmpty } from '../../utils/helpers'
import UseTranslation from '../../hooks/UseTranslation'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'
import LoadMoreBtn from '../PageHelpers/LoadMoreBtn'

const OfoqMultiAutoCompleteWithLoadMore = ({
  name,
  store,
  defaultOption,
  refState,
  nameConverter,
  required,
  customStyle,
  loadMoreActionToDispatch,
  searchActionToDispatch,
  actionQueries = {},
  multiple = true,
  customPlaceHolder,
  customSearchPlaceholder,
  reduxStore,
  ...props
}) => {
  const splitName = name?.split('_')?.join(' ');
  const [options, setOptions] = useState(store || []);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const { t } = UseTranslation();
  const dispatch = useDispatch()
  const anchorRef = useRef(null)

  useEffect(() => {
    setOptions(store || []);
  }, [store]);

  const sendSelectedItem = (value) => {
    refState(value);
  };

  const handleInputChange = useCallback(
    debounce((newValue) => {
      dispatch(searchActionToDispatch({ queries: newValue, actionQueries }))
    }, 600),
    [searchActionToDispatch],
  );

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const handleClose = (_, reason) => {
    if (reason === 'toggleInput') {
      setOpen(false)
    }
  }

  const onClickAway = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  return (
    <FormControl sx={styles.formControlGrow} style={customStyle}
      ref={anchorRef}
    >
      <InputLabel
        htmlFor={name}
        sx={styles.formInputLabel}
      >
        {splitName}
        {required && <TeamSnap style={styles.snapIcon} data-testid="teamsnap-icon" />}
      </InputLabel>
      <Autocomplete
        open={open}
        onClose={handleClose}
        onOpen={() => setOpen(true)}
        id={name}
        limitTags={1}
        defaultValue={isNotEmpty(defaultOption) ? defaultOption : []}
        value={isNotEmpty(defaultOption) ? defaultOption : []}
        onKeyDown={(e) => { e.preventDefault(); }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={styles.multiAutoComplete}
        options={options || []}
        getOptionLabel={(option) => option.name || option.english_name || ''}
        popupIcon={<KeyboardArrowDownIcon />}
        onChange={(_, newValue) => sendSelectedItem(newValue)}
        getOptionDisabled={() => false}
        multiple={multiple}
        loading={reduxStore?.loading}
        loadingText={t?.Loading}
        noOptionsText={`${t?.NOT} ${splitName} ${t?.FOUND}`}
        ListboxProps={{
          sx: {
            maxHeight: '300px',
          },
        }}
        disableCloseOnSelect={true}
        renderOption={(props2, option, { selected, index }) => (
          <>
          <Box component="li" {...props2} sx={[{ mt: index === 0 ? '5px' : '0px' }, styles.option]}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              sx={styles.checkBoxStyles}
              checked={selected}
            />
            {nameConverter
              ? nameConverter(option)
              : option.name || option.english_name}
          </Box>
          {index === (options?.length || 0) - 1 && options?.length &&

          <LoadMoreBtn
            store={reduxStore}
            actionToDispatch={loadMoreActionToDispatch}
            actionQueries={actionQueries}
            customStyles={{ mb: '0px' }}
          />
          }
          </>

        )}
        PaperComponent={({ children }) => (
          <ClickAwayListener onClickAway={onClickAway}>

            <Paper key={'Paper2'}
              anchorEl={anchorRef?.current}
            >
              <ListSubheader sx={{ position: 'inherit' }}>
                <TextField
                  placeholder={customSearchPlaceholder || `search for ${splitName}`}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    handleInputChange(e.target.value);
                  }}
                  key={'search-in-db'}
                  autoFocus
                  variant="outlined"
                  fullWidth
                  margin="dense"
                  name={`${splitName}`}
                />
              </ListSubheader>
              {children}
            </Paper>

          </ClickAwayListener>
        )}
        renderInput={(params) => (
          <TextField
            placeholder={`${t?.Select} ${customPlaceHolder || splitName}`}
            variant="outlined"
            fullWidth
            margin="dense"
            disabled
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {(reduxStore?.loading) ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        {...props}
      />
    </FormControl>
  );
};

const styles = {
  formControlGrow: {
    width: '100%',
    marginBottom: { xs: '0px', sm: '20px' },
    '& .MuiInputLabel-outlined.MuiInputLabel-marginDense': {
      transform: 'translate(14px,12px) scale(1)',
    },
    '& .MuiFormControl-marginDense': {
      marginTop: { xs: '5px', sm: '30px' },
      marginBottom: '0',
      paddingRight: '0',
    },
    '& .MuiOutlinedInput-root': {
      marginTop: { xs: '10px', sm: 'unset' },
      borderRadius: 4,
      backgroundColor: '#fafbfc',
      height: { xs: '40px', sm: 'unset' },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#29cc8b',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'solid 2px #e2e8ee',
      transition: 'all 0.3s ease-in-out',
    },
    '& .MuiOutlinedInput-inputMarginDense': {
      padding: { xs: '8px', sm: '12px' },
    },
    '& .MuiAlert-root': {
      width: '100%',
      marginBottom: { xs: 12, sm: 16 },
    },
    '& .MuiOutlinedInput-input': {
      fontSize: { xs: '12px', sm: '14px' },
    },
  },

  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    textTransform: 'capitalize',
    color: 'common.black',
    fontSize: { xs: '11px', md: '16px' },
    width: 'max-content',
    marginBottom: { xs: '10px', sm: '5px' },
    paddingRight: { xs: '14px', sm: '18px' },
    '& svg': {
      mx: '10px',
    },
  },
  multiAutoComplete: {
    '& .MuiChip-label': {
      fontSize: { xs: '10px', sm: '0.875rem' },
      paddingLeft: { xs: '4px', sm: '8px' },
      paddingRight: { xs: '4px', sm: '8px' },
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      height: { xs: '21px', sm: '25px' },
      color: '#000',
    },

    '& .MuiChip-deleteIcon': {
      color: '#29cc8b',
      fill: '#29cc8b',
      width: '18px',
      margin: '0px 6px 0px -2px',
      cursor: 'pointer',
    },

    '& .MuiSvgIcon-root': {
      fill: 'currentColor',
      display: 'inline-block',
      fontSize: { xs: '1rem', sm: '1.5rem' },
      transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      flexShrink: 0,
      userSelect: 'none',
    },

    '& .MuiAutocomplete-tag': {
      paddingRight: '5px',
      paddingBottom: '3px',
      borderRadius: '10px',
      border: 'solid 1px rgba(41, 204, 139, 0.59)',
      backgroundColor: '#edfff8',
      '& .MuiChip-deleteIcon': { fill: '#29cc8b' },
    },
    '& input': {
      fontSize: '14px',
      height: {
        xs: '4px',
        sm: '20px',
      },
    },
  },
  formInput: {
    height: { xs: '40px', sm: 'unset' },
  },
  option: {
    fontSize: { xs: '14px', sm: '16px' },
    fontFamily: 'Avenir',
  },
  checkBoxStyles: {
    fontSize: '14px !important',
  },
}

export default OfoqMultiAutoCompleteWithLoadMore
